import { SelectedSolution } from '../xstate/prostudio.xstate.types';

/**
 * PSTUDIO-4121. Decide whether `ctx.selectedBot` still describes the solution
 * the context now names.
 *
 * Why this is needed: a browser POP (Back / trackpad swipe) from solution B to
 * solution A updates `ctx.botName` through the route loader but dispatches no
 * `setSelectedSolution`, so `ctx.selectedBot` keeps holding B. `loadBotVersions`
 * skips its `getBotById` whenever a `selectedBot` merely EXISTS, and then builds
 * a hybrid — B's versions and draft version under A's name.
 *
 * The comparison is on `botName`, never on `id`. On every healthy path the two
 * are equal by construction: `prostudio.xstate.ts:1590` assigns
 * `ctx.botName = data.selectedSolution?.botName` in the same action that assigns
 * `ctx.selectedBot`. An id comparison would not hold that way — a
 * `botSolutionId` is a lookup value rather than a concatenation of
 * `${customerId}.${botName}` (`resolve-bot-solution-id.ts`), so it could read as
 * stale on a healthy load and cost a request every time.
 *
 * The question is about the COPY, never about its presence: with no copy at all
 * there is nothing that could be stale, so the answer is `false` and deciding
 * whether to fetch is the caller's (`load-bot-versions.ts` spells that out as
 * `!ctxSelectedBot || ctxSelectedBotIsStale`). A future caller writing the
 * natural `if (isSelectedBotStale(...)) refetch()` would therefore skip the
 * fetch in exactly the case that always needs one — which is why the two
 * conditions belong together at every call site.
 *
 * Two edges are answered fail-safe, i.e. STALE, because the cost of being wrong
 * is asymmetric — an extra `GetBotById` is one request, while trusting an object
 * that cannot be identified is the hybrid this ticket is about:
 *
 * - the copy carries no `botName` of its own. This is also self-healing: when a
 *   load ends without bot data the copy that lands in context has no `botName`,
 *   so the next pass cannot adopt it and must re-fetch;
 * - the context names no solution. That one is unreachable from
 *   `loadBotVersions`, which returns early on a missing `botName`
 *   (`load-bot-versions.ts`, `throwErrorWithId('Bot Name is required')`), so the
 *   answer only binds a future caller.
 *
 * BOTH names are trimmed before comparing. Note this is deliberately stricter
 * than `isSolutionNotFound` (`solution-not-found.ts`), which trims the incoming
 * value and compares it against an untrimmed `s?.name` — the two therefore
 * disagree on padded data, and the precedent is named here to mark the
 * difference rather than to claim parity.
 */
export function isSelectedBotStale(args: {
  selectedBot?: Pick<SelectedSolution, 'botName'> | null;
  botName?: string | null;
}): boolean {
  const { selectedBot, botName } = args;

  if (!selectedBot) return false;

  const current = botName?.trim();
  if (!current) return true;

  const owned = selectedBot.botName?.trim();
  if (!owned) return true;

  return owned !== current;
}
